import { Grid } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  AgencySettings_EnabledFeaturesEnum,
  PolicyTimeslot_DaysEnum,
  PolicyRule_VehicleTypesEnum,
  PolicyTimeslot,
} from 'src/api/gen/api'
import { IPolicyRuleGeofence } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { isEndTimeBeforeStartTime } from 'src/commons/policies/utils'
import { SimpleTable } from 'src/components/Table'
import { useCheckEnabledFeature } from 'src/layout/AgencySettings'
import SETTINGS from 'src/settings'

import { FieldTitle } from '../../commons'
import { GeographiesField, RuleNameField, VehicleTypesField } from '../fields'

import TimeSlotForm from './TimeSlot'

type Row = [
  TRulesValidation,
  string, // name
  string, // ruleId
  PolicyRule_VehicleTypesEnum[], // vehicle_types
  string[], // geographies
  string[] // buffered_geographies
]

function GeofenceRule(props: IBaseTableProps<IPolicyRuleGeofence>) {
  const { rules, allGeographies, disabled, validation } = props
  const { t } = useTranslation()
  const buffered_areas_enabled = useCheckEnabledFeature()(AgencySettings_EnabledFeaturesEnum.buffered_areas)

  const HEADERS: [string, string?][] = [
    [t('rule.name'), undefined],
    [t('rule.count.vehicle_types'), undefined],
    [t('rule.geographies'), t('rule.geographies.tooltip')],
  ]

  if (buffered_areas_enabled) {
    HEADERS.push([t('rule.buffered_geographies'), t('rule.buffered_geographies.tooltip')])
  }

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleGeofence) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRule: IPolicyRuleGeofence[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      props.handleChange(newRule)
    }

  function handleChangeTimeslot(timeslot: PolicyTimeslot, index: number, key: keyof PolicyTimeslot) {
    return (val: string | PolicyTimeslot_DaysEnum[] | null): void => {
      const timeslotsToUpdate: PolicyTimeslot[] = props.rules[0].timeslots
      timeslotsToUpdate[index] = { ...timeslot, [key]: val }
      const newRules: IPolicyRuleGeofence[] = [{ ...props.rules[0], ['timeslots']: timeslotsToUpdate }]
      props.handleChange(newRules)
    }
  }

  const items = rules.map(
    (rule: IPolicyRuleGeofence, idx): Row => [
      validation[idx],
      rule.name,
      rule.rule_id,
      rule.vehicle_types,
      rule.geographies,
      rule.buffered_geographies,
    ]
  )
  return (
    <>
      <SimpleTable
        headerRow={HEADERS.map(([title, tooltip]) => (
          <TableCell key={`key-${title}`}>
            <FieldTitle title={title} tooltip={tooltip} />
          </TableCell>
        ))}
        items={items}
        rowRenderer={([ruleValidation, name, ruleId, vehicleTypes, geographies, buffered_geographies]: Row) => (
          <>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
              <RuleNameField
                id="geofencing_fields_name"
                value={name}
                handleChange={handleChangeRule(ruleId, 'name')}
                disabled={disabled}
                error={!ruleValidation.isFieldValid('name')}
                helperText={ruleValidation.getFieldErrorMsg('name')}
              />
            </TableCell>

            <TableCell style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
              <VehicleTypesField
                id="geofencing_fields_vehicle_type"
                value={vehicleTypes}
                handleChange={handleChangeRule(ruleId, 'vehicle_types')}
                disabled={disabled}
                error={!ruleValidation.isFieldValid('vehicle_types')}
                helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
              />
            </TableCell>

            <TableCell
              align="justify"
              style={{
                maxWidth: '400px',
                height: '100px',
                verticalAlign: 'bottom',
              }}
            >
              <GeographiesField
                id="geofencing_fields_geography"
                value={geographies}
                allGeographies={allGeographies}
                handleChange={handleChangeRule(ruleId, 'geographies')}
                disabled={disabled}
                error={!ruleValidation.isFieldValid('geographies')}
                helperText={ruleValidation.getFieldErrorMsg('geographies')}
              />
            </TableCell>

            {buffered_areas_enabled && (
              <TableCell
                align="justify"
                style={{
                  maxWidth: '400px',
                  height: '100px',
                  verticalAlign: 'bottom',
                }}
              >
                <GeographiesField
                  id="prohibited_parking_buffered_geography"
                  value={buffered_geographies}
                  allGeographies={allGeographies}
                  handleChange={handleChangeRule(ruleId, 'buffered_geographies')}
                  disabled={disabled}
                  helperText={t('rule.buffered_geographies.help')}
                />
              </TableCell>
            )}
          </>
        )}
      />
      {SETTINGS.app.enable_timeslots && (
        <Grid container spacing={3}>
          {props.rules[0].timeslots.map((timeslot, index) => {
            return (
              <Grid item xs={3} key={index}>
                <Grid container direction="column" spacing={1} style={{ marginTop: '13px' }}>
                  <Grid item component="h3">
                    {t('rule.time_slot_count', { index: index + 1 })}
                  </Grid>
                  <Grid item>
                    <TimeSlotForm
                      timeslot={timeslot}
                      handleChangeDays={handleChangeTimeslot(timeslot, index, 'days')}
                      handleChangeStartTime={handleChangeTimeslot(timeslot, index, 'start_time')}
                      handleChangeEndTime={handleChangeTimeslot(timeslot, index, 'end_time')}
                      handleRemoveTimeSlot={() => props.handleRemoveTimeSlot(index)}
                      disabled={props.isPublished && props.disabled}
                      error={isEndTimeBeforeStartTime(timeslot)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      )}
    </>
  )
}

export default GeofenceRule
